import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { InfoDrawerProps } from '@/components/Layout/Drawer/Drawer.interface';
import { InfoDrawer } from '@/components/Layout/Drawer/InfoDrawer';

type ConfirmationOptions = Omit<InfoDrawerProps, 'onClose'> & {
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
};

type ConfirmationContextType = {
  showConfirmation: (options: ConfirmationOptions) => void;
  hideConfirmation: () => void;
};

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(
  undefined,
);

type ConfirmationProviderProps = {
  children: ReactNode;
};

export const ConfirmationProvider = ({
  children,
}: ConfirmationProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationOptions | null>(null);

  const showConfirmation = useCallback(
    (confirmationOptions: ConfirmationOptions) => {
      setOptions(confirmationOptions);
      setIsOpen(true);
    },
    [],
  );

  const hideConfirmation = useCallback(() => {
    setIsOpen(false);
    setOptions(null);
  }, []);

  const handlePrimaryAction = useCallback(async () => {
    if (options?.onConfirm) {
      await options.onConfirm();
    }
    if (options?.primaryAction) {
      options.primaryAction();
    }
    hideConfirmation();
  }, [options, hideConfirmation]);

  const handleSecondaryAction = useCallback(async () => {
    if (options?.onCancel) {
      await options.onCancel();
    }
    if (options?.secondaryAction) {
      options.secondaryAction();
    }
    hideConfirmation();
  }, [options, hideConfirmation]);

  const handleClose = useCallback(() => {
    if (options?.onCancel) {
      options.onCancel();
    }
    hideConfirmation();
  }, [options, hideConfirmation]);

  const contextValue = useMemo(
    () => ({ showConfirmation, hideConfirmation }),
    [showConfirmation, hideConfirmation],
  );

  return (
    <ConfirmationContext.Provider value={contextValue}>
      {children}
      {isOpen && options && (
        <InfoDrawer
          title={options.title}
          showHeader={options.showHeader}
          description={options.description}
          drawerImage={options.drawerImage}
          content={options.content}
          primaryAction={handlePrimaryAction}
          primaryText={options.primaryText}
          secondaryAction={
            options.secondaryAction || options.onCancel
              ? handleSecondaryAction
              : undefined
          }
          secondaryText={options.secondaryText}
          secondaryButtonProps={options.secondaryButtonProps}
          primaryButtonProps={options.primaryButtonProps}
          containerProps={options.containerProps}
          onClose={handleClose}
        />
      )}
    </ConfirmationContext.Provider>
  );
};

ConfirmationContext.displayName = 'ConfirmationContext';

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);

  if (!context) {
    throw new Error(
      'useConfirmation must be used within a ConfirmationProvider',
    );
  }

  return context;
};

export default ConfirmationContext;
